import { useEffect, useMemo, useState } from "react";
import { Checkbox, Form, Input, InputNumber, message, Select, Typography, type SelectProps } from "antd";
import { type FormInstance } from "antd/lib/form";
import orderBy from "lodash.orderby";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";

import { programBuilderActions } from "@fitness-app/app-store";
import { TimePerWeek, TrainingLimitation } from "@fitness-app/data-models/entities/ClientProfile";
import { type ProgramAutomation, type WorkoutRoutine } from "@fitness-app/data-models/entities/ProgramAutomation";
import { ClientExperience, PlaceToWorkout } from "@fitness-app/data-models/entities/SurveyTemplate";
import {
  type TrainingProgramDetails,
  type TrainingProgramWithCreator,
} from "@fitness-app/data-models/entities/TrainingProgram";
import { getName } from "@fitness-app/data-models/utils/getAuthorName";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import {
  type SelectProgramFormModel,
  type WorkoutRoutineWithDates,
} from "~/modules/Automation/ScheduleProgramStepper/steps/types";
import { useTrainingPlanSearch } from "~/modules/TrainingPrograms/hooks/useTrainingPlanSearch";
import { useAppDispatch } from "~/store/initializeStore";

type DefaultOptionType = NonNullable<SelectProps["options"]>[number];

interface SelectProgramProps {
  selectedAutomation: ProgramAutomation;
  onSubmit: (model: SelectProgramFormModel) => void;
  formController: FormInstance<SelectProgramFormModel>;
  workoutRoutineModel?: WorkoutRoutine | WorkoutRoutineWithDates | null;
  withoutWeeksLimit?: boolean;
}

const SelectProgram = ({
  selectedAutomation,
  onSubmit,
  formController,
  workoutRoutineModel,
  withoutWeeksLimit,
}: SelectProgramProps) => {
  const { t } = useTranslation(["automation", "common"]);
  const dispatch = useAppDispatch();
  const [fetchedPlan, setFetchedPlan] = useState<{
    plan: TrainingProgramWithCreator;
    details: TrainingProgramDetails;
  } | null>(null);
  const { data, isLoading, setSearchValue } = useTrainingPlanSearch();
  const { userId } = useUserRole();

  useEffect(() => {
    if (workoutRoutineModel) {
      formController.setFieldsValue({
        program: workoutRoutineModel.name,
        startWeek: workoutRoutineModel.startWeek,
        endWeek: workoutRoutineModel.endWeek,
        requireFullFirstWeek: workoutRoutineModel.requireFullFirstWeek ?? null,
        matchRules: workoutRoutineModel.matchRules ?? null,
      });
    }

    return () => {
      formController.resetFields();
    };
  }, [workoutRoutineModel]);

  const onProgramSelect = async (_programId: string, option: DefaultOptionType) => {
    if ("key" in option) {
      try {
        const plan = await dispatch(
          programBuilderActions.validateProgramWithDetails({
            id: option.key as string,
          }),
        ).unwrap();
        setFetchedPlan(plan);
      } catch (error) {
        void message.error(typeof error === "string" ? error : "Nie udało się pobrać planu treningowego.");
      }
    }
  };

  const currentNumberOfScheduledWeeks = Object.keys(selectedAutomation.schedule).length;

  const handleSubmit = (model: SelectProgramFormModel) => {
    onSubmit({
      ...model,
      selectedProgram: fetchedPlan?.plan || null,
    });
  };

  const planOptions = useMemo(() => {
    if (!data) {
      return [];
    }
    if (!userId) {
      return data.map((plan) => ({
        label: plan.name,
        value: plan.id,
        key: plan.id,
        createdBy: plan.createdBy,
        creator: plan.creator,
        tags: plan.tags,
      }));
    }
    const ordered = orderBy(
      data.map((plan) => ({
        label: plan.name,
        value: plan.id,
        key: plan.id,
        createdBy: plan.createdBy,
        creator: plan.creator,
        tags: plan.tags,
      })),
      (item) => item.createdBy === userId,
      "desc",
    );

    return ordered;
  }, [data, userId]);

  const onPlanSearch = useDebouncedCallback((value: string) => {
    setSearchValue(value);
  }, 300);

  return (
    <div>
      <Form<SelectProgramFormModel>
        name="schedule-program"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        form={formController}
        onFinish={handleSubmit}
        labelWrap
        initialValues={{
          program: "",
          startWeek: 1,
          endWeek: currentNumberOfScheduledWeeks,
          additionalComment: "",
        }}
      >
        <Form.Item
          name="program"
          label="Wybierz program"
          validateStatus={isLoading ? "validating" : undefined}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select
            optionLabelProp="label"
            optionFilterProp="label"
            allowClear
            showSearch
            disabled={!!workoutRoutineModel}
            onSelect={onProgramSelect}
            onClear={() => {
              setFetchedPlan(null);
              formController?.setFieldsValue({
                program: "",
              });
            }}
            onSearch={onPlanSearch}
          >
            {planOptions.map((option) => (
              <Select.Option value={option.value} label={option.label} key={option.value}>
                <div className={cn("flex flex-col gap-y-2")}>
                  <span>{option.label}</span>
                  <Typography.Text className="text-xs" type="secondary">
                    Autor: {"creator" in option ? getName(option.creator, undefined, false) : "-"}
                  </Typography.Text>
                  {option.tags?.length ? (
                    <Typography.Text className="text-xs" type="secondary">
                      Tagi: {option.tags?.join(", ")}
                    </Typography.Text>
                  ) : null}
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <>
          <Form.Item
            name="startWeek"
            label="Od tygodnia"
            rules={[
              {
                required: true,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
            ]}
          >
            <InputNumber min={1} max={withoutWeeksLimit ? undefined : currentNumberOfScheduledWeeks} />
          </Form.Item>

          <Form.Item<SelectProgramFormModel>
            noStyle
            shouldUpdate={(prev, current) => prev.startWeek !== current.startWeek}
          >
            {({ getFieldValue }) => {
              const startWeek = getFieldValue("startWeek") as number;

              if (startWeek === 1) {
                return (
                  <Form.Item
                    name="requireFullFirstWeek"
                    tooltip="Jeśli zaznaczysz to pole, program zostanie automatycznie rozszerzony o pełny tydzień aby zakończył się w niedzielę"
                    label="Wymagaj zakończenie na koniec tygodnia"
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>

          <Form.Item
            name="endWeek"
            label="Do tygodnia"
            rules={[
              {
                required: true,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
            ]}
          >
            <InputNumber min={1} max={withoutWeeksLimit ? undefined : currentNumberOfScheduledWeeks} />
          </Form.Item>
        </>

        <Form.Item name="additionalComment" label="Uwagi do planu">
          <Input.TextArea />
        </Form.Item>

        <Typography.Title level={5}>Dopasowanie do profilu klientki (opcjonalne)</Typography.Title>
        <Typography.Paragraph type="secondary">
          Plany z tym samym tygodniem startu tworzą warianty — automatyzacja wybierze wariant pasujący do profilu z
          ankiety. Puste pole = wariant pasuje do wszystkich. Plan bez reguł jest wariantem domyślnym.
        </Typography.Paragraph>

        <Form.Item name={["matchRules", "placeToWorkout"]} label="Miejsce treningu">
          <Select
            mode="multiple"
            allowClear
            placeholder="Dowolne"
            options={[
              { value: PlaceToWorkout.HOME, label: "Dom bez sprzętu" },
              { value: PlaceToWorkout.HOME_WITH_EQUIPMENT, label: "Dom z podstawowym sprzętem" },
              { value: PlaceToWorkout.GYM, label: "Siłownia" },
            ]}
          />
        </Form.Item>

        <Form.Item name={["matchRules", "experience"]} label="Doświadczenie">
          <Select
            mode="multiple"
            allowClear
            placeholder="Dowolne"
            options={[
              { value: ClientExperience.NEWBIES, label: "Zaczyna od zera" },
              { value: ClientExperience.MEDIUM, label: "Wraca po przerwie" },
              { value: ClientExperience.EXPERT, label: "Trenuje regularnie" },
            ]}
          />
        </Form.Item>

        <Form.Item name={["matchRules", "timePerWeek"]} label="Czas na trening">
          <Select
            mode="multiple"
            allowClear
            placeholder="Dowolny"
            options={[
              { value: TimePerWeek.TwoToThree, label: "2-3 treningi / tydzień" },
              { value: TimePerWeek.FourToFive, label: "4-5 treningów / tydzień" },
              { value: TimePerWeek.DailyShort, label: "Codziennie, krótko" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name={["matchRules", "excludedLimitations"]}
          label="Wyklucz przy ograniczeniach"
          tooltip="Wariant NIE zostanie przydzielony klientce, która zaznaczyła w ankiecie któreś z tych ograniczeń"
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Brak wykluczeń"
            options={[
              { value: TrainingLimitation.Knees, label: "Kolana" },
              { value: TrainingLimitation.Spine, label: "Kręgosłup" },
              { value: TrainingLimitation.Hips, label: "Biodra" },
              { value: TrainingLimitation.ShouldersOrWrists, label: "Barki / nadgarstki" },
              { value: TrainingLimitation.Hypertension, label: "Nadciśnienie" },
              { value: TrainingLimitation.PregnancyOrPostpartum, label: "Ciąża / połóg" },
            ]}
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default SelectProgram;
